import {
    UdioWrapperConfig,
    SongOptions,
    UdioResponse,
    CompleteSequenceOptions,
    SequenceResponse,
    SongStatusResponse,
    FetchImplementation
} from '../types';
import { safeParseJson, validateRequiredParams } from './utils';

export class UdioWrapper {
    private authToken: string;
    private apiUrl: string;
    private fetchImplementation: FetchImplementation;

    /**
     * Create a new Udio Wrapper
     * 
     * @param authToken - The Udio API authorization token
     * @param config - Configuration options
     */
    constructor(authToken: string, config: UdioWrapperConfig = {}) {
        this.authToken = authToken;
        this.apiUrl = config.apiUrl || 'https://api.udio.com/v1';
        this.fetchImplementation = config.fetchImplementation || fetch;

        if (!authToken) {
            throw new Error('Authentication token is required');
        }
    }

    /**
     * Execute a request to the Udio API
     * 
     * @param endpoint - API endpoint path
     * @param options - Fetch options
     * @returns Promise with the parsed JSON response
     */
    private async request<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<T> {
        const headers = {
            'Authorization': `Bearer ${this.authToken}`,
            'Content-Type': 'application/json',
            ...options.headers,
        };

        const response = await this.fetchImplementation(`${this.apiUrl}${endpoint}`, {
            ...options,
            headers,
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const errorMessage = errorData.error || `API request failed with status ${response.status}`;
            throw new Error(errorMessage);
        }

        return safeParseJson<T>(response);
    }

    /**
     * Create a new song
     * 
     * @param options - Song creation options
     * @returns Promise with the song response
     */
    async createSong(options: SongOptions): Promise<UdioResponse> {
        validateRequiredParams(options, ['prompt']);

        const payload = {
            prompt: options.prompt,
            seed: options.seed ?? -1,
            custom_lyrics: options.customLyrics || null,
        };

        return this.request<UdioResponse>('/songs', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    /**
     * Extend an existing song
     * 
     * @param options - Song extension options
     * @returns Promise with the extended song response
     */
    async extendSong(options: SongOptions): Promise<UdioResponse> {
        validateRequiredParams(options, [
            'prompt',
            'audioConditioningPath',
            'audioConditioningSongId'
        ]);

        const payload = {
            prompt: options.prompt,
            seed: options.seed ?? -1,
            audio_conditioning_path: options.audioConditioningPath,
            audio_conditioning_song_id: options.audioConditioningSongId,
            custom_lyrics: options.customLyrics || null,
        };

        return this.request<UdioResponse>('/songs/extend', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    /**
     * Add an outro to an existing song
     * 
     * @param options - Outro creation options
     * @returns Promise with the outro song response
     */
    async addOutro(options: SongOptions): Promise<UdioResponse> {
        validateRequiredParams(options, [
            'prompt',
            'audioConditioningPath',
            'audioConditioningSongId'
        ]);

        const payload = {
            prompt: options.prompt,
            seed: options.seed ?? -1,
            audio_conditioning_path: options.audioConditioningPath,
            audio_conditioning_song_id: options.audioConditioningSongId,
            custom_lyrics: options.customLyrics || null,
        };

        return this.request<UdioResponse>('/songs/outro', {
            method: 'POST',
            body: JSON.stringify(payload),
        });
    }

    /**
     * Create a complete song sequence (short song + extensions + outro)
     * 
     * @param options - Sequence creation options
     * @returns Promise with the sequence response
     */
    async createCompleteSequence(options: CompleteSequenceOptions): Promise<SequenceResponse> {
        validateRequiredParams(options, ['shortPrompt', 'extendPrompts', 'numExtensions']);

        if (options.numExtensions !== options.extendPrompts.length) {
            throw new Error('The number of extend prompts must match numExtensions');
        }

        if (options.customLyricsExtend &&
            options.customLyricsExtend.length !== options.numExtensions) {
            throw new Error('The number of custom lyrics must match numExtensions');
        }

        // Create short song
        const shortSong = await this.createSong({
            prompt: options.shortPrompt,
            customLyrics: options.customLyricsShort,
        });

        // Create extensions
        const extensions: UdioResponse[] = [];
        let lastSong = shortSong;

        for (let i = 0; i < options.numExtensions; i++) {
            const extendedSong = await this.extendSong({
                prompt: options.extendPrompts[i],
                audioConditioningPath: lastSong.url,
                audioConditioningSongId: lastSong.id,
                customLyrics: options.customLyricsExtend?.[i],
            });

            extensions.push(extendedSong);
            lastSong = extendedSong;
        }

        // Create outro if requested
        let outro: UdioResponse | undefined;
        if (options.outroPrompt) {
            outro = await this.addOutro({
                prompt: options.outroPrompt,
                audioConditioningPath: lastSong.url,
                audioConditioningSongId: lastSong.id,
                customLyrics: options.customLyricsOutro,
            });
        }

        return {
            short: shortSong,
            extensions,
            outro,
        };
    }

    /**
     * Get the status of a song
     * 
     * @param songId - ID of the song to check
     * @returns Promise with the song status
     */
    async getSongStatus(songId: string): Promise<SongStatusResponse> {
        if (!songId) {
            throw new Error('Song ID is required');
        }

        return this.request<SongStatusResponse>(`/songs/${songId}`);
    }

    /**
     * Wait for a song to be completed
     * 
     * @param songId - ID of the song to wait for
     * @param options - Polling options
     * @returns Promise with the completed song status
     */
    async waitForCompletion(
        songId: string,
        options: {
            pollingInterval?: number;
            maxAttempts?: number;
            onProgress?: (status: SongStatusResponse) => void;
        } = {}
    ): Promise<SongStatusResponse> {
        const pollingInterval = options.pollingInterval || 5000;
        const maxAttempts = options.maxAttempts || 60;

        let attempts = 0;

        while (attempts < maxAttempts) {
            const status = await this.getSongStatus(songId);

            if (options.onProgress) {
                options.onProgress(status);
            }

            if (status.status !== 'processing') {
                return status;
            }

            await new Promise(resolve => setTimeout(resolve, pollingInterval));
            attempts++;
        }

        throw new Error('Timed out waiting for song completion');
    }
}